import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useMemo } from 'react';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import ViewTenantDetailsModal from './ViewTenantDetailsModal';
import EditTenantModal from './EditTenantModal';
import AuditLogModal from './AuditLogModal';
import DeleteConfirmModal from './DeleteConfirmModal';

const ManageTenantListLayer = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [societyFilter, setSocietyFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editTenant, setEditTenant] = useState(null);
    const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
    const [auditLogTenant, setAuditLogTenant] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [tenantToDelete, setTenantToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Sample tenant data with demo names
    const tenantsData = [
        { id: 1, name: 'Rajesh Kumar', flatNumber: '301', building: 'Tower A', society: 'Onboarding Society', phone: '9876543210', email: 'rajesh.kumar@email.com', status: true, createdOn: '2024-01-15' },
        { id: 2, name: 'Priya Sharma', flatNumber: '402', building: 'Tower B', society: 'Onboarding Society', phone: '9123456789', email: 'priya.sharma@email.com', status: true, createdOn: '2024-01-16' },
        { id: 3, name: 'Amit Patel', flatNumber: '105', building: 'Tower A', society: 'Onboarding Society', phone: '8765432109', email: 'amit.patel@email.com', status: true, createdOn: '2024-01-17' },
        { id: 4, name: 'Neha Gupta', flatNumber: '501', building: 'Tower C', society: 'Onboarding Society', phone: '9234567890', email: 'neha.gupta@email.com', status: false, createdOn: '2024-01-18' },
        { id: 5, name: 'Vikram Singh', flatNumber: '203', building: 'Tower B', society: 'Onboarding Society', phone: '9345678901', email: 'vikram.singh@email.com', status: true, createdOn: '2024-01-19' },
        { id: 6, name: 'Anjali Verma', flatNumber: '602', building: 'Tower C', society: 'Onboarding Society', phone: '9456789012', email: 'anjali.verma@email.com', status: true, createdOn: '2024-01-20' },
        { id: 7, name: 'Arjun Desai', flatNumber: '304', building: 'Tower A', society: 'Onboarding Society', phone: '9567890123', email: 'arjun.desai@email.com', status: true, createdOn: '2024-01-21' },
        { id: 8, name: 'Divya Malhotra', flatNumber: '405', building: 'Tower B', society: 'Onboarding Society', phone: '9678901234', email: 'divya.malhotra@email.com', status: true, createdOn: '2024-01-22' },
        { id: 9, name: 'Rohit Menon', flatNumber: '503', building: 'Tower C', society: 'Onboarding Society', phone: '9789012345', email: 'rohit.menon@email.com', status: false, createdOn: '2024-01-23' },
        { id: 10, name: 'Kavya Nair', flatNumber: '106', building: 'Tower A', society: 'Onboarding Society', phone: '9890123456', email: 'kavya.nair@email.com', status: true, createdOn: '2024-01-24' },
    ];

    // Get unique societies for filter dropdown
    const societiesList = ['Onboarding Society'];

    // Filtering logic
    const filteredTenants = useMemo(() => {
        return tenantsData.filter(tenant => {
            const searchMatch = tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tenant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tenant.flatNumber.toLowerCase().includes(searchTerm.toLowerCase());
            const statusMatch = !statusFilter || (statusFilter === 'active' ? tenant.status : !tenant.status);

            return searchMatch && statusMatch;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, statusFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);
    const paginatedTenants = filteredTenants.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Modal handlers
    const handleViewClick = (tenant) => {
        setSelectedTenant(tenant);
        setViewModalOpen(true);
    };

    const handleEditClick = (tenant) => {
        setEditTenant(tenant);
        setEditModalOpen(true);
    };

    const handleAuditLogClick = (tenant) => {
        setAuditLogTenant(tenant);
        setAuditLogModalOpen(true);
    };

    const handleDeleteClick = (tenant) => {
        setTenantToDelete(tenant);
        setDeleteModalOpen(true);
    };

    const handleStatusChange = (tenant) => {
        // TODO: Implement status update API call
        console.log('Update status for:', tenant.id);
    };

    const handleClear = () => {
        setSearchTerm('');
        setSocietyFilter('');
        setStatusFilter('');
        setCurrentPage(1);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            // TODO: Implement delete API call
            console.log('Deleting tenant:', tenantToDelete.id);
            setDeleteModalOpen(false);
            // Refresh tenant list after deletion
        } catch (error) {
            console.error('Error deleting tenant:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, filteredTenants.length);

    return (
        <div className="card">
            {/* Filter Section */}
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="tw-w-48">
                        <SearchableDropdown
                            value={societyFilter || 'all'}
                            onChange={(v) => { setSocietyFilter(v === 'all' ? '' : v); setCurrentPage(1); }}
                            options={[
                                { value: 'all', label: 'All Communities' },
                                ...societiesList.map((s) => ({ value: s, label: s })),
                            ]}
                            placeholder="Community"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={statusFilter || 'all'}
                            onChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setCurrentPage(1); }}
                            options={[
                                { value: 'all', label: 'All Status' },
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ]}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>

                    {/* Search by Name */}
                    <div className="icon-field">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search by name, email, or flat number"
                            className="form-control form-control-sm w-auto"
                        />
                        <span className="icon">
                            <Icon icon="ion:search-outline" />
                        </span>
                    </div>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3">
                    {/* Clear Button */}
                    <button
                        onClick={handleClear}
                        className="btn btn-sm"
                        style={{
                            backgroundColor: '#CCCCCC',
                            color: 'white',
                            border: '1px solid #AAAAAA',
                        }}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Table Section */}
            <div className="card-body p-0">
                <table className="table bordered-table mb-0">
                    <thead>
                        <tr>
                            <th scope="col">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                    />
                                    <label className="form-check-label">
                                        #
                                    </label>
                                </div>
                            </th>
                            <th scope="col">Tenant Name</th>
                            <th scope="col">Flat Number</th>
                            <th scope="col">Building</th>
                            <th scope="col">Email</th>
                            <th scope="col">Phone</th>
                            <th scope="col">Status</th>
                            <th scope="col">Created On</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedTenants.length > 0 ? (
                            paginatedTenants.map((tenant, index) => (
                                <tr key={tenant.id}>
                                    <td>
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                            />
                                            <label className="form-check-label">
                                                {startIndex + index}
                                            </label>
                                        </div>
                                    </td>
                                    <td className="text-md fw-medium">{tenant.name}</td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {tenant.flatNumber}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {tenant.building}
                                        </div>
                                    </td>
                                    <td>{tenant.email}</td>
                                    <td>{tenant.phone}</td>
                                    <td>
                                        <Switch
                                            checked={tenant.status}
                                            onChange={() => handleStatusChange(tenant)}
                                            size="small"
                                        />
                                    </td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {new Date(tenant.createdOn).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td>
                                        {/* Edit Button */}
                                        <Tooltip title="Edit" placement="top" arrow>
                                            <button
                                                onClick={() => handleEditClick(tenant)}
                                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="lucide:edit" />
                                            </button>
                                        </Tooltip>

                                        {/* View Details Button */}
                                        <Tooltip title="View" placement="top" arrow>
                                            <button
                                                onClick={() => handleViewClick(tenant)}
                                                className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="iconamoon:eye-light" />
                                            </button>
                                        </Tooltip>

                                        {/* Audit Log Button */}
                                        <Tooltip title="Audit Log" placement="top" arrow>
                                            <button
                                                onClick={() => handleAuditLogClick(tenant)}
                                                className="w-32-px h-32-px me-8 bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="solar:clipboard-list-bold" />
                                            </button>
                                        </Tooltip>

                                        {/* Delete Button */}
                                        <Tooltip title="Delete" placement="top" arrow>
                                            <button
                                                onClick={() => handleDeleteClick(tenant)}
                                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="mingcute:delete-2-line" />
                                            </button>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center py-4">
                                    <p className="text-gray-600">No tenants found</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Section */}
            <div className="card-footer d-flex flex-wrap justify-content-between align-items-center gap-3 ps-2">
                <div className="text-sm text-gray-600">
                    {filteredTenants.length > 0 ? (
                        <>
                            Showing {startIndex} to {endIndex} of {filteredTenants.length} tenants
                        </>
                    ) : (
                        'No tenants'
                    )}
                </div>
                <PaginationLimit
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={(value) => {
                        setItemsPerPage(value);
                        setCurrentPage(1);
                    }}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                />
            </div>

            {/* Modals */}
            <ViewTenantDetailsModal
                open={viewModalOpen}
                handleClose={() => {
                    setViewModalOpen(false);
                    setSelectedTenant(null);
                }}
                tenant={selectedTenant}
            />

            <EditTenantModal
                open={editModalOpen}
                handleClose={() => {
                    setEditModalOpen(false);
                    setEditTenant(null);
                }}
                tenant={editTenant}
            />

            <AuditLogModal
                open={auditLogModalOpen}
                handleClose={() => {
                    setAuditLogModalOpen(false);
                    setAuditLogTenant(null);
                }}
                tenant={auditLogTenant}
            />

            <DeleteConfirmModal
                open={deleteModalOpen}
                handleClose={() => {
                    setDeleteModalOpen(false);
                    setTenantToDelete(null);
                }}
                handleConfirm={handleDeleteConfirm}
                tenant={tenantToDelete}
                isLoading={isDeleting}
            />
        </div>
    );
};

export default ManageTenantListLayer;
